import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

// Define the types for NOC Ex-India Request
export interface NOCExIndiaRequest {
  id: number;
  is_draft?: boolean;
  status?: string;
  referenceNumber: string;
  empID?: string;
  empId?: string;
  approverUserID: string;
  employeeName?: string;
  placeOfVisit?: string;
  placeOfVisitNoc?: string;
  ePlaceOfVisitNoc?: string;
  purposeOfVisit?: string;
  ePurposeOfVisit?: string;
  periodOfVisitFrom?: string;
  ePeriodOfVisitFrom?: string;
  periodOfVisitTo?: string;
  ePeriodOfVisitTo?: string;
  leaveDetails?: string;
  eLeaveDetails?: string;
  leaveType?: string;
  totalExpenditure?: string | number;
  totalMembersTravel?: string | number;
  stayingExpenditure?: string;
  travelExpenditureA?: string;
  otherExpenditureAm?: string;
  currency?: string;
  forTravel?: string;
  forStay?: string;
  forTravelName?: string;
  forTravelRelation?: string;
  forStayName?: string;
  forStayRelation?: string;
  tExpenditureBorneBy?: string;
  tRelativesName?: string;
  tRelationWithPerson?: string;
  sExpenditureBorneBy?: string;
  sRelativesName?: string;
  sRelationWithPerson?: string;
  expenditureCertification?: boolean;
  hasNotAvailedExIndiaLeave?: boolean;
  chknil?: string;
  dsarPending?: string;
  vigilancePending?: string;
  dsarStatus?: string;
  dnARStatus?: string;
  dnARPending?: string;
  dandARStatus?: string;
  dnARPenaltyEffecti?: string;
  penaltyEffectiveFrom?: string;
  remarks?: string;
  hRemarks?: string;
  concernedDirector?: string;
  requestCreationDate?: string;
  requestStatus?: string;
  lastLatestApprover?: string;
  remarksIfRejected?: string;
  sapCreatedAt?: string;
  sapModifiedAt?: string;
  action?: string;
  eDepartment?: string;
  created_at: string;
  modified_at: string;
  countriesToVisit?: string;
  durationOfVisit?: string;
  uploadedFilesNote?: string;
  pay_scale?: string;

  // Additional fields
  department?: string;
  contactNumber?: string;
  email?: string;
  dateOfBirth?: string;
  dateOfJoining?: string;
  designation?: string;
  employmentStatus?: string;
  age?: string;
  category?: string;
  lwp?: string;

  // Related data
  travelEntries?: Array<{
    Seq: string;
    Country: string;
    DurationOfVisit: string;
    PurposeOfVisit: string;
  }>;

  previousVisits?: Array<{
    id: number;
    countryVisited: string;
    durationFrom: string;
    durationTo: string;
    purposeOfVisit: string;
  }>;

  documents?: Array<{
    id: number;
    referenceNumber?: string;
    ex_india_request_id?: number;
    empID?: string;
    fileName: string;
    typeOfAttachedDoc: string;
    fileExtension?: string;
    fileSize?: string;
    content?: string;
    documentUploadedBy?: string;
    sap_created_at?: string;
    sap_modified_at?: string;
    created_at?: string;
    modified_at?: string;
    deleted_at?: string;
  }>;

  // Employee details from join
  employeeDetails?: {
    empId: string;
    fullName?: string;
    firstname?: string;
    secondname?: string;
    image?: string;
    email?: string;
    phone?: string;
  };
}

export interface NOCExIndiaState {
  requests: NOCExIndiaRequest[];
  currentRequest: NOCExIndiaRequest | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  uploadLoading: boolean;
  deleteLoading: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Define the API response type
interface NOCExIndiaResponse {
  data: NOCExIndiaRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Define the request parameters
interface FindAllNOCExIndiaParams {
  page?: number;
  limit?: number;
  orderColumn?: string;
  orderBy?: string;
  query?: string;
  requestStatus?: string;
  startDate?: string;
  endDate?: string;
}

// Define the create NOC request parameters
interface CreateNOCExIndiaParams {
  id?: number;
  is_draft?: boolean;
  placeOfVisit?: string;
  purposeOfVisit?: string;
  periodOfVisitFrom?: string;
  periodOfVisitTo?: string;
  leaveDetails?: string;
  leaveType?: string;
  totalExpenditure?: string | number;
  forTravel?: string;
  forStay?: string;
  forTravelName?: string;
  forTravelRelation?: string;
  forStayName?: string;
  forStayRelation?: string;
  expenditureCertification?: boolean;
  hasNotAvailedExIndiaLeave?: boolean;
  dsarPending?: string;
  vigilancePending?: string;
  previousVisits?: Array<{
    countryVisited: string;
    durationFrom: string;
    durationTo: string;
    purposeOfVisit: string;
  }>;
  documents?: Array<{
    fileName: string;
    typeOfAttachedDoc?: string;
  }>;
}

// File upload parameters
interface FileUploadParams {
  filestring: string;
}

// File delete parameters
interface FileDeleteParams {
  key: string;
}

// NOC request delete parameters
interface DeleteNOCExIndiaParams {
  id: number;
}

// Find one NOC request parameters
interface FindOneNOCExIndiaParams {
  id: number;
}

// Initial state
const initialState: NOCExIndiaState = {
  requests: [],
  currentRequest: null,
  loading: false,
  error: null,
  createLoading: false,
  uploadLoading: false,
  deleteLoading: false,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

// Async thunk for fetching travel list
export const fetchTravelList = createAsyncThunk(
  "nocExIndia/fetchTravelList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.SAP_MASTER_NOC_TRAVEL_LIST,
        {}
      );
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for fetching stay list
export const fetchStayList = createAsyncThunk(
  "nocExIndia/fetchStayList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.NOC.SAP_MASTER_STAY_LIST, {});
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for fetching NOC ex-india requests
export const fetchNOCExIndiaRequests = createAsyncThunk(
  "nocExIndia/fetchRequests",
  async (params: FindAllNOCExIndiaParams = {}, { rejectWithValue }) => {
    try {
      console.log("Fetching NOC Ex-India requests with params:", params);
      const response = await Api.post(
        API_URLS.NOC.FIND_ALL_EX_INDIA_REQUESTS,
        params
      );
      console.log("Raw API response:", response);

      // Handle the nested response structure
      const responseData = response.data;
      console.log("Response data:", responseData);

      if (responseData && responseData.data) {
        const innerData = responseData.data;
        console.log("Inner data structure:", innerData);

        if (innerData && typeof innerData === "object" && "data" in innerData) {
          console.log("Found nested data structure");

          const extractedData = {
            data: innerData.data || [],
            total: innerData.total || 0,
            page: innerData.page || 1,
            limit: innerData.limit || 10,
            totalPages: innerData.totalPages || 0,
          };
          console.log("Final extracted data:", extractedData);
          return extractedData as NOCExIndiaResponse;
        } else if (Array.isArray(innerData)) {
          // Case: responseData.data is directly the array
          const extractedData = {
            data: innerData,
            total: innerData.length,
            page: 1,
            limit: innerData.length,
            totalPages: 1,
          };
          console.log(
            "Extracted data for Redux (direct array):",
            extractedData
          );
          return extractedData as NOCExIndiaResponse;
        } else {
          console.error("Unexpected data structure:", innerData);
          throw new Error("Unexpected API response data structure");
        }
      } else {
        console.error("Invalid API response structure:", responseData);
        throw new Error("Invalid API response structure");
      }
    } catch (error: any) {
      console.error("API call failed:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for creating/updating NOC ex-india request
export const createNOCExIndiaRequest = createAsyncThunk(
  "nocExIndia/createRequest",
  async (params: CreateNOCExIndiaParams, { rejectWithValue }) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.CREATE_EX_INDIA_REQUEST,
        params
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for fetching single NOC ex-india request
export const fetchNOCExIndiaRequest = createAsyncThunk(
  "nocExIndia/fetchRequest",
  async (params: FindOneNOCExIndiaParams, { rejectWithValue }) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ONE_EX_INDIA_REQUEST,
        params
      );

      // Handle nested response structure
      const responseData = response.data;
      if (responseData && responseData.data) {
        return responseData; // Return the whole response with data property
      }

      // If response.data is directly the object, wrap it
      return { data: responseData };
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for uploading file to AWS
export const uploadFileToAWS = createAsyncThunk(
  "nocExIndia/uploadFile",
  async (params: FileUploadParams, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for deleting file from AWS
export const deleteFileFromAWS = createAsyncThunk(
  "nocExIndia/deleteFile",
  async (params: FileDeleteParams, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.AWS_BUCKET.DELETE_FILE, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for deleting NOC ex-india request
export const deleteNOCExIndiaRequest = createAsyncThunk(
  "nocExIndia/deleteRequest",
  async (params: DeleteNOCExIndiaParams, { rejectWithValue }) => {
    try {
      const requestBody = {
        id: params.id,
      };
      const response = await Api.post(
        API_URLS.NOC.DELETE_EX_INDIA_REQUEST,
        requestBody
      );
      return { ...response.data, deletedId: params.id };
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create the slice
const nocExIndiaSlice = createSlice({
  name: "nocExIndia",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentRequest: (state) => {
      state.currentRequest = null;
    },
    setCurrentRequest: (state, action: PayloadAction<number>) => {
      const requestId = action.payload;
      const request = state.requests.find((req) => req.id === requestId);
      state.currentRequest = request || null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch NOC ex-india requests
      .addCase(fetchNOCExIndiaRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNOCExIndiaRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchNOCExIndiaRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create NOC ex-india request
      .addCase(createNOCExIndiaRequest.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createNOCExIndiaRequest.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createNOCExIndiaRequest.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single NOC ex-india request
      .addCase(fetchNOCExIndiaRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNOCExIndiaRequest.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both nested and direct data structures
        if (action.payload && action.payload.data) {
          state.currentRequest = action.payload.data;
        } else {
          state.currentRequest = action.payload;
        }
      })
      .addCase(fetchNOCExIndiaRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload file to AWS
      .addCase(uploadFileToAWS.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadFileToAWS.fulfilled, (state) => {
        state.uploadLoading = false;
      })
      .addCase(uploadFileToAWS.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload as string;
      })
      // Delete file from AWS
      .addCase(deleteFileFromAWS.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteFileFromAWS.fulfilled, (state) => {
        state.deleteLoading = false;
      })
      .addCase(deleteFileFromAWS.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      })
      // Delete NOC ex-india request
      .addCase(deleteNOCExIndiaRequest.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteNOCExIndiaRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove the deleted request from the list
        state.requests = state.requests.filter(
          (req) => req.id !== action.payload.deletedId
        );
        // Update pagination total
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      })
      .addCase(deleteNOCExIndiaRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearError,
  clearCurrentRequest,
  setCurrentRequest,
  setPage,
  setLimit,
} = nocExIndiaSlice.actions;

// Export reducer
export default nocExIndiaSlice.reducer;
